import dayjs from "dayjs";

const FONT = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";
export const CHART_MIN_HEIGHT = 240;

const ORANGE = "#f97316";
const BLUE = "#3b82f6";
const PINK = "#ec4899";
const GREEN = "#22c55e";
const RED = "#ef4444";
const GRAY = "#94a3b8";
const GRID_COLOR = "#e2e8f0";
const AXIS_COLOR = "#475569";

export const OPERATOR_CHART_ORANGE = ORANGE;
export const STATUS_BREAKDOWN_LABELS = ["Active", "Inactive", "Expired"];
export const STATUS_BREAKDOWN_COLORS = [GREEN, ORANGE, GRAY];

const chartBase = {
  chart: {
    toolbar: { show: false },
    animations: { enabled: true, easing: "easeinout", speed: 600 },
    fontFamily: FONT,
  },
  grid: {
    borderColor: GRID_COLOR,
    strokeDashArray: 4,
    padding: { left: 4, right: 12 },
  },
  dataLabels: { enabled: false },
  stroke: { width: 2, curve: "smooth" },
  tooltip: {
    theme: "light",
    style: { fontSize: "12px", fontFamily: FONT },
    borderColor: GRID_COLOR,
  },
  legend: {
    fontSize: "12px",
    fontFamily: FONT,
    labels: { colors: AXIS_COLOR },
  },
  xaxis: { labels: { style: { colors: AXIS_COLOR, fontSize: "12px" } } },
  yaxis: { labels: { style: { colors: AXIS_COLOR, fontSize: "12px" } } },
};

export function extractDashboardPayload(data) {
  return data?.data ?? data ?? {};
}

export function parsePolicyTrend(raw) {
  if (!raw) return { trendCategories: [], trendValues: [] };
  return {
    trendCategories: raw.labels ?? [],
    trendValues: (raw.data ?? []).map(Number),
  };
}

export function parsePolicyStatusBreakdown(raw) {
  if (!raw) {
    return { statusLabels: STATUS_BREAKDOWN_LABELS, statusValues: [0, 0, 0] };
  }
  return {
    statusLabels: raw.labels ?? STATUS_BREAKDOWN_LABELS,
    statusValues: (raw.data ?? []).map(Number),
  };
}

export function parseTravellerDemographics(raw) {
  if (!raw) return { ageCategories: [], maleSeries: [], femaleSeries: [] };
  return {
    ageCategories: raw.labels ?? [],
    maleSeries: (raw.male ?? []).map(Number),
    femaleSeries: (raw.female ?? []).map(Number),
  };
}

const categoryLabelAt = (categories, val, opts) => {
  const idx = opts?.dataPointIndex ?? opts?.i;
  if (idx != null && categories[idx] != null) return categories[idx];
  return val;
};

const trendTickAmount = (count, timeFrame) => {
  if (timeFrame === "D") return Math.min(count, 7);
  if (timeFrame === "M") return Math.min(count, 12);
  return Math.min(count, 8);
};

export function parseWalletUsage(raw) {
  if (!raw) {
    return { walletCategories: [], rechargeSeries: [], spendSeries: [] };
  }
  return {
    walletCategories: raw.labels ?? [],
    rechargeSeries: (raw.credit ?? []).map(Number),
    spendSeries: (raw.debit ?? []).map(Number),
  };
}

export function parsePaymentTrend(raw) {
  if (!raw) return { paymentCategories: [], paymentValues: [] };
  return {
    paymentCategories: raw.labels ?? [],
    paymentValues: (raw.data ?? []).map(Number),
  };
}

const SUMMARY_CARD_META = {
  wallet_balance: { label: "Wallet Balance", tone: "green", group: "summary" },
  wallet_used: { label: "Wallet Used", tone: "blue", group: "summary" },
  policy_purchase_amount: { label: "Policy Purchase", tone: "amber", group: "summary" },
  policies_issued: { label: "Policies Issued", tone: "coral", group: "summary" },
  active: { label: "Active Policies", tone: "teal", group: "policy_statistics" },
  inactive: { label: "Inactive Policies", tone: "orange", group: "policy_statistics" },
  expired: { label: "Expired Policies", tone: "indigo", group: "policy_statistics" },
};

export function parsePolicyStatistics(raw) {
  if (!raw) {
    return {
      active: 0,
      inactive: 0,
      expired: 0,
      foot: {},
    };
  }
  return {
    active: Number(raw.active ?? 0),
    inactive: Number(raw.inactive ?? 0),
    expired: Number(raw.expired ?? 0),
    foot: raw.foot ?? {},
  };
}

export function parseOperatorSummaryCards(summary, policyStatisticsRaw) {
  const policyStatistics = parsePolicyStatistics(policyStatisticsRaw);

  return Object.entries(SUMMARY_CARD_META).map(([key, meta]) => {
    if (meta.group === "policy_statistics") {
      return {
        key,
        label: meta.label,
        tone: meta.tone,
        value: policyStatistics[key] ?? 0,
        currency: null,
        foot: policyStatistics.foot?.[key] ?? "",
        trendPercent: null,
        lowBalanceAlert: false,
        walletApplicable: true,
      };
    }

    const item = summary?.[key] ?? {};
    return {
      key,
      label: meta.label,
      tone: meta.tone,
      value: item.value ?? 0,
      currency: item.currency ?? null,
      foot: item.foot ?? "",
      // trendPercent: item.trend_percent ?? null,
      lowBalanceAlert: Boolean(item.low_balance_alert),
      walletApplicable: item.wallet_applicable !== false,
    };
  });
}

/** Match x-axis label formatting to API label granularity, not just the selected view. */
export function resolveChartLabelView(categories = [], selectedView = "D") {
  const first = String(categories[0] ?? "");
  if (/^\d{4}-\d{2}-\d{2}$/.test(first)) return "D";
  if (/^\d{4}-\d{2}$/.test(first)) return "M";
  return selectedView;
}

export const fmtTrendLabel = (val, timeFrame) => {
  if (val == null || val === "") return "";
  const s = String(val);
  const wMatch = s.match(/[Ww](\d+)/);
  if (wMatch) return `Wk ${wMatch[1]}`;
  const mMatch = s.match(/^(\d{4})-(\d{2})$/);
  if (mMatch) {
    return dayjs(`${mMatch[1]}-${mMatch[2]}-01`).format("MMM 'YY");
  }
  const dMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dMatch) {
    const d = dayjs(s);
    if (!d.isValid()) return s;
    return d.format("DD MMM");
  }
  const d = dayjs(s);
  if (d.isValid()) {
    return timeFrame === "M" ? d.format("MMM 'YY") : d.format("DD MMM");
  }
  return s;
};

const buildPeriodChartXaxis = (categories, timeFrame) => ({
  ...chartBase.xaxis,
  categories,
  tickAmount: trendTickAmount(categories.length, timeFrame),
  labels: {
    rotate: timeFrame === "D" ? -40 : -20,
    formatter: (val, _ts, opts) =>
      fmtTrendLabel(categoryLabelAt(categories, val, opts), timeFrame),
    hideOverlappingLabels: true,
  },
});

const buildPeriodChartTooltip = (categories, timeFrame) => ({
  ...chartBase.tooltip,
  x: {
    formatter: (val, { dataPointIndex }) =>
      fmtTrendLabel(categoryLabelAt(categories, val, { dataPointIndex }), timeFrame),
  },
  y: { formatter: (v) => String(Math.round(v)) },
});

export function buildTrendChartOptions(trendCategories, timeFrame) {
  return {
    ...chartBase,
    chart: { ...chartBase.chart, type: "area" },
    colors: [ORANGE],
    markers: {
      size: trendCategories.length <= 14 ? 4 : 0,
      strokeColors: ORANGE,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.4,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [0, 90, 100],
      },
    },
    xaxis: {
      ...chartBase.xaxis,
      categories: trendCategories,
      tickAmount: Math.min(trendCategories.length, timeFrame === "D" ? 7 : 8),
      labels: {
        rotate: timeFrame === "D" ? -40 : -20,
        formatter: (v) => fmtTrendLabel(v, timeFrame),
        hideOverlappingLabels: true,
      },
    },
    yaxis: {
      ...chartBase.yaxis,
      labels: {
        style: { colors: AXIS_COLOR, fontSize: "12px" },
        formatter: (v) => Math.round(v),
      },
    },
    tooltip: {
      ...chartBase.tooltip,
      y: { formatter: (v) => String(Math.round(v)) },
      x: { formatter: (v) => fmtTrendLabel(v, timeFrame) },
    },
  };
}

export function buildPolicyStatusBreakdownOptions(statusLabels) {
  return {
    ...chartBase,
    chart: { ...chartBase.chart, type: "bar" },
    colors: STATUS_BREAKDOWN_COLORS,
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: "45%",
        distributed: true,
      },
    },
    xaxis: { ...chartBase.xaxis, categories: statusLabels },
    legend: { show: false },
    tooltip: {
      ...chartBase.tooltip,
      y: { formatter: (v) => String(Math.round(v)) },
    },
  };
}

export function buildAgeGenderChartOptions(ageCategories) {
  return {
    ...chartBase,
    chart: { ...chartBase.chart, type: "bar" },
    colors: [BLUE, PINK],
    plotOptions: { bar: { columnWidth: "48%", borderRadius: 4 } },
    xaxis: { ...chartBase.xaxis, categories: ageCategories },
    legend: { ...chartBase.legend, position: "top" },
  };
}

export function buildWalletUsageOptions(categories, timeFrame) {
  const labelView = resolveChartLabelView(categories, timeFrame);
  return {
    ...chartBase,
    chart: { ...chartBase.chart, type: "area" },
    colors: [GREEN, RED],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.3,
        opacityFrom: 0.35,
        opacityTo: 0.02,
        stops: [0, 90, 100],
      },
    },
    xaxis: buildPeriodChartXaxis(categories, labelView),
    yaxis: {
      ...chartBase.yaxis,
      labels: {
        style: { colors: AXIS_COLOR, fontSize: "12px" },
        formatter: (v) => Math.round(v),
      },
    },
    legend: { ...chartBase.legend, position: "top" },
    tooltip: buildPeriodChartTooltip(categories, labelView),
  };
}

export function buildPaymentTrendOptions(categories, timeFrame) {
  const labelView = resolveChartLabelView(categories, timeFrame);
  return {
    ...chartBase,
    chart: { ...chartBase.chart, type: "bar" },
    colors: [ORANGE],
    plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
    xaxis: buildPeriodChartXaxis(categories, labelView),
    yaxis: {
      ...chartBase.yaxis,
      labels: {
        style: { colors: AXIS_COLOR, fontSize: "12px" },
        formatter: (v) => Math.round(v),
      },
    },
    legend: { show: false },
    tooltip: buildPeriodChartTooltip(categories, labelView),
  };
}

export function hasChartSeries(values = []) {
  return values.length > 0;
}

export function hasAnyPositive(values = []) {
  return values.some((value) => Number(value) > 0);
}
